import React, { useState, useEffect } from "react";
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import api from '../../service/caller';

const EditableBlockList = ({ onsavesuggestion }) => {
  const { transcript, resetTranscript, listening } = useSpeechRecognition();
  const [summary, setSummary] = useState('');
  const [suggestions, setSuggestions] = useState('');
  const [loading, setLoading] = useState(false);
  const [isManualInput, setIsManualInput] = useState(false);
  const [manualText, setManualText] = useState('');
  const [error, setError] = useState('');
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (!SpeechRecognition.browserSupportsSpeechRecognition()) {
      setError("Votre navigateur ne supporte pas la reconnaissance vocale");
    }
  }, []);

  const handleRecording = () => {
    if (listening) {
      SpeechRecognition.stopListening();
    } else {
      resetTranscript();
      SpeechRecognition.startListening({ continuous: true, language: 'fr-FR' });
    }
  };

  const handleManualTextChange = (e) => {
    setManualText(e.target.value);
  };

  const handleClearText = () => {
    if (isManualInput) {
      setManualText('');
    } else {
      resetTranscript();
    }
    setSummary('');
    setSuggestions('');
    setShowSaveConfirmation(false);
    setSaveSuccess(false);
  };

  const handleProcessText = async () => {
    setLoading(true);
    try {
      const response = await api.post("/api/aiprocesstext", {
        text: isManualInput ? manualText : transcript,
      });
      setSummary(response.data.summary);
      setSuggestions(response.data.suggestions);
      setShowSaveConfirmation(true); // Afficher la confirmation de sauvegarde après le traitement
    } catch (error) {
      console.error("Erreur:", error);
      setError(error.response?.data?.error || "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSuggestions = () => {
    if (onsavesuggestion && typeof onsavesuggestion === 'function') {
      onsavesuggestion({
        suggestions
      });
      setSaveSuccess(true); // Indiquer que la sauvegarde a réussi
      setShowSaveConfirmation(false); // Cacher la confirmation après la sauvegarde
    }
  };

  const formatSuggestions = (suggestions) => {
    if (!suggestions) return '';

    const formattedSuggestions = [];
    const lines = suggestions.split('\n');

    lines.forEach(line => {
      // Cas 1: Titre principal (potentiellement en gras) suivi de ":"
      const titleMatchColon = line.match(/^\*\*\s*(.+?):\s*(.*)$/);
      if (titleMatchColon) {
        const title = titleMatchColon[1].trim();
        const content = titleMatchColon[2].trim();
        formattedSuggestions.push(
          <div key={`suggestion-${formattedSuggestions.length}`}>
            <h6 className="suggestion-title" style={{ fontWeight: 'bold' }}>{title}</h6>
            {content && <p className="suggestion-text" style={{ textAlign: 'justify' }}>{content}</p>}
          </div>
        );
        return; // Passer à la ligne suivante
      }

      // Cas 2: Titre de liste (gras et souligné) précédé de "* **"
      const listItemTitleMatch = line.match(/^\*\s*\*\*\s*(.+?)\s*\*\*\s*(.*)$/);
      if (listItemTitleMatch) {
        const title = listItemTitleMatch[1].trim();
        const content = listItemTitleMatch[2].trim();
        formattedSuggestions.push(
          <div key={`suggestion-${formattedSuggestions.length}`}>
            <h6 className="suggestion-title" style={{ fontWeight: 'bold', textDecoration: 'underline' }}>{title}</h6>
            {content && <p className="suggestion-text" style={{ textAlign: 'justify' }}>{content}</p>}
          </div>
        );
        return; // Passer à la ligne suivante
      }

      // Cas 3: Ligne de texte simple (paragraphe)
      const paragraphMatch = line.trim();
      if (paragraphMatch) {
        formattedSuggestions.push(
          <p key={`paragraph-${formattedSuggestions.length}`} className="suggestion-text" style={{ textAlign: 'justify' }}>{paragraphMatch}</p>
        );
      }
    });

    return formattedSuggestions;
  };

  return (
    <div className="container py-4" style={{ width: '80%' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <button
            className="btn btn-success text-white px-4 py-2 me-2"
            style={{ background: '#1A5D1A' }}
            onClick={handleRecording}
            disabled={isManualInput}
          >
            {listening ? 'Arrêter l\'enregistrement' : 'Démarrer l\'enregistrement'}
            <i className="bi bi-mic ms-2"></i>
          </button>

          <button
            className="btn btn-secondary px-4 py-2"
            onClick={handleClearText}
          >
            Effacer
            <i className="bi bi-trash ms-2"></i>
          </button>
        </div>

        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="inputTypeSwitch"
            checked={isManualInput}
            onChange={() => setIsManualInput(!isManualInput)}
          />
          <label className="form-check-label" htmlFor="inputTypeSwitch">
            Saisie manuelle
          </label>
        </div>
      </div>

      <div className="mb-4 mt-3 p-3" style={{ border: '1px dashed #0056B3', borderRadius: '10px' }}>
        <div className="text-center py-2 mb-3 fw-bold" style={{ background: listening ? '#FF980017' : '#f8f9fa', borderRadius: '5px' }}>
          {listening ? 'Écoute en cours...' : (isManualInput ? 'Mode saisie manuelle' : 'Prêt à enregistrer')}
        </div>

        {isManualInput ? (
          <textarea
            value={manualText}
            onChange={handleManualTextChange}
            rows="5"
            className="form-control mb-3"
            placeholder="Saisissez votre texte ici..."
          />
        ) : (
          <div className="mb-3 p-3" style={{
            minHeight: '100px',
            backgroundColor: '#f8f9fa',
            borderRadius: '5px',
            border: '1px solid #dee2e6'
          }}>
            {transcript || 'La transcription apparaîtra ici...'}
          </div>
        )}

        {error && <div className="alert alert-danger">{error}</div>}

        <button
          onClick={handleProcessText}
          className="btn btn-primary mt-2 w-100"
          disabled={loading || (!transcript && !manualText)}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Traitement en cours...
            </>
          ) : 'Obtenir le résumé et les suggestions'}
        </button>

        {summary && (
          <div className="mt-4 p-3" style={{ backgroundColor: '#e6f7ff', borderRadius: '5px' }}>
            <h5 className="mb-3">Résumé:</h5>
            <p className="mb-0">{summary}</p>
          </div>
        )}

        {suggestions && (
          <div className="mt-4 p-4 bg-light rounded-3 border-start border-4 border-primary">
            <h5 className="text-primary mb-4 d-flex align-items-center">
              <i className="bi bi-clipboard2-pulse me-2"></i>
              Recommandations médicales
            </h5>
            <div className="suggestion-content">
              {formatSuggestions(suggestions)} {/* Appel de la fonction ici */}
            </div>
          </div>
        )}
      </div>

      {showSaveConfirmation && (
        <div className="mt-4 border-top pt-3">
          <p className="mb-2 fw-semibold">Souhaitez-vous enregistrer ces suggestions ?</p>
          <div className="d-flex gap-2">
            <button onClick={handleSaveSuggestions} className="btn btn-success">Oui, enregistrer</button>
            <button onClick={() => setShowSaveConfirmation(false)} className="btn btn-outline-secondary">Non, annuler</button>
          </div>
        </div>
      )}

      {saveSuccess && (
        <div className="alert alert-success mt-3">
          Suggestions enregistrées avec succès !
        </div>
      )}
    </div>
  );
};

export default EditableBlockList;
